import React from 'react';
import {
  Grid,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getEmailCampaign } from '../../endpoints/emailCampaigns';
import type { EmailCampaign } from '../../endpoints/emailCampaigns';
import AlertItem from '../../Components/AlertItem';
import dayjs from 'dayjs';

const EmailCampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: campaign,
    isLoading,
    error,
  } = useApiGet<EmailCampaign>(
    ['email-campaign', id!],
    () => getEmailCampaign(id!),
    { enabled: !!id, retry: 1 }
  );

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    try {
      return dayjs(dateString).format('MMM DD, YYYY h:mm A');
    } catch {
      return '—';
    }
  };

  if (!id) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Grid item xs={12} padding={2}>
          <Typography color="error">Missing campaign ID.</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/administration/email-campaigns')}
          >
            Back to list
          </Button>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Grid item xs={12} padding={2}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error || !campaign) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Grid item xs={12} padding={2}>
          <AlertItem
            severity="error"
            description="Campaign not found or failed to load."
            onClose={() => {}}
            keepOpen
          />
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/administration/email-campaigns')}
          >
            Back to list
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/administration/email-campaigns')}
          sx={{ mb: 2 }}
        >
          Back to list
        </Button>
        <Typography variant="h4" color="primary">
          {campaign.name}
        </Typography>
        <Chip
          label={campaign.status}
          size="small"
          sx={{ mt: 1 }}
          color="primary"
          variant="outlined"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Details
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell variant="head" width="180">
                  Template ID
                </TableCell>
                <TableCell>{campaign.template_id || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Subject</TableCell>
                <TableCell>{campaign.subject || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Audiences</TableCell>
                <TableCell>{campaign.audience_ids || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Estimated recipients</TableCell>
                <TableCell>
                  {campaign.estimated_recipient_count ?? '—'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Recipient count</TableCell>
                <TableCell>{campaign.recipient_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Scheduled at</TableCell>
                <TableCell>{formatDateTime(campaign.scheduled_at)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Sent at</TableCell>
                <TableCell>{formatDateTime(campaign.sent_at)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Created at</TableCell>
                <TableCell>{formatDateTime(campaign.created_at)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            SendGrid stats
          </Typography>
          {campaign.last_stats_at ? (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Last updated: {formatDateTime(campaign.last_stats_at)}
            </Typography>
          ) : null}
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell variant="head" width="180">
                  Sent
                </TableCell>
                <TableCell>{campaign.sent_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Delivered</TableCell>
                <TableCell>{campaign.delivered_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Opened</TableCell>
                <TableCell>{campaign.opened_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Clicked</TableCell>
                <TableCell>{campaign.clicked_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Bounces</TableCell>
                <TableCell>{campaign.bounce_count ?? '—'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {!campaign.last_stats_at && (
            <Typography variant="body2" color="text.secondary">
              Stats are updated by a daily sync from SendGrid.
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EmailCampaignDetail;
